import {useFormik} from 'formik'
import {isEqual} from 'lodash'
import React, {useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import UseYupValidate from '../../../../../../_gori/hooks/UseYupValidate'
import {
  Button,
  InputEmailFormik,
  ValidationErrorModal,
} from '../../../../../../_gori/partials/widgets'
import {InputCheckboxFormik} from '../../../../../../_gori/partials/widgets/form/InputCheckboxFormik'
import {ConfigGeneral} from '../../../core/_const'
import SettingsService from '../../../core/_requests'
import {StorageHelpers} from '../../../../../../_gori/helpers'

type Props = {
  show: boolean
  handleClose: () => void
  data?: any
  handleReload: any
}

const ClaimAlertModal: React.FC<Props> = ({show, handleClose, data, handleReload}) => {
  const intl = useIntl()
  const {infoYup} = UseYupValidate()
  const {newCancelToken} = useCancelToken()
  const [validationErrors, setValidationErrors] = useState<any>()

  const validationSchema = Yup.object().shape({
    status: Yup.boolean(),
    emails: Yup.array().when('status', {
      is: (val) => val,
      then: Yup.array()
        .min(1, intl.formatMessage({id: 'REQUIRED'}))
        .max(
          ConfigGeneral.MAX_EMAIL_NUMBER,
          intl.formatMessage(
            {id: 'ONLY_UP_TO_INPUT_EMAIL_ADDRESSES_ARE_ALLOWED'},
            {input: ConfigGeneral.MAX_EMAIL_NUMBER}
          )
        ),
    }),
    email: Yup.string().when('status', {
      is: (val) => val,
      then: infoYup.email('EMAIL', false).test({
        name: 'unique',
        test: function (value) {
          const emails = this.parent.emails || []
          return !emails?.map((item) => item?.toLowerCase()).includes(value?.toLowerCase())
        },
        message: intl.formatMessage(
          {id: 'INPUT_ALREADY_EXISTS'},
          {input: intl.formatMessage({id: 'EMAIL'})}
        ),
      }),
    }),
  })

  const formik = useFormik({
    initialValues: {
      status: data?.status ?? false,
      emails: data?.receiver?.email?.filter((item) => item) ?? [],
      email: '',
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values, {setSubmitting}) => {
      try {
        const arrCurrentEmail = values.emails
        const initEmail = data?.receiver?.email

        const config = {cancelToken: newCancelToken()}
        const payload = {
          category: data?.category ?? ConfigGeneral.NOTIFICATION_CATEGORY.CLAIMS,
          type: data?.type ?? ConfigGeneral.NOTIFICATION_TYPE.EMAIL,
          account_id: data?.account_id,
          status: values.status,
          receiver: {
            email:
              !isEqual(initEmail, arrCurrentEmail) && !values.status
                ? data?.receiver?.email
                : arrCurrentEmail,
          },
        }
        setSubmitting(true)
        const res = data?.id
          ? await SettingsService.updateNotification(data?.id, payload, config)
          : await SettingsService.createNotification(payload, config)
        if (res) {
          toast.success(intl.formatMessage({id: res.message}))
          handleReload()
          handleCloseModal()
        }
      } catch (error: any) {
        setValidationErrors(error?.response)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleCloseModal = () => {
    handleClose()
    formik.resetForm()
  }

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}

      <Modal
        id='gori_modal_change_password'
        tabIndex={-1}
        aria-hidden='true'
        centered
        dialogClassName='modal-size-md h-auto'
        contentClassName='modal-padding'
        show={show}
        backdrop='static'
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title bsPrefix={'fw-bolder fs-1'}>
            {intl.formatMessage({id: 'CLAIM_ALERTS'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className='overflow-auto'>
          <div className='d-flex justify-content-between'>
            <label htmlFor='status' className='form-check-label form-label'>
              {intl.formatMessage({id: 'ALERT'})}
            </label>
            <InputCheckboxFormik
              formik={formik}
              type='switch'
              name='status'
              disabled={formik.isSubmitting}
            />
          </div>
          {formik.values.status && (
            <div className='mt-4'>
              <InputEmailFormik
                label={intl.formatMessage({id: 'EMAIL_TO'})}
                required={true}
                formik={formik}
                maxEmailNumber={ConfigGeneral.MAX_EMAIL_NUMBER}
                configAppend={{
                  name: intl.formatMessage({id: 'ADD'}),
                  position: 'right',
                  classInput: 'pe-25',
                  hasEvent: true,
                }}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className='d-flex justify-content-end'>
            <Button
              className='btn btn-advanced-secondary me-2'
              label={intl.formatMessage({id: 'CANCEL'})}
              loadingText={intl.formatMessage({id: 'CANCEL'})}
              event={handleCloseModal}
              disabled={formik.isSubmitting}
            />
            <Button
              className='btn btn-advanced-primary'
              label={intl.formatMessage({id: 'SAVE'})}
              loadingText={intl.formatMessage({id: 'SAVE'})}
              disabled={formik.isSubmitting}
              event={() => {
                StorageHelpers.setItemLocalStorage('cardnotification', {hidden: true})
                window.dispatchEvent(new Event('storage'))
                formik.submitForm()
              }}
              loading={formik.isSubmitting}
            />
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export {ClaimAlertModal}
